import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { useAuth, usePrefs } from "@/lib/context";
import { LANGS } from "@/lib/i18n";
import { useCategories, catName } from "@/lib/hooks";
import { PROFILES, FIELD_LABEL, DEFAULT_HERO } from "@/lib/profiles";
import { MfaStep } from "@/components/MfaStep";

const L = (lang) => lang;

function GoogleButton({ role }) {
  const { t } = usePrefs();
  const go = () => {
    localStorage.setItem("gst_signup_role", role);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={go} data-testid="google-login-button"
      className="flex h-11 w-full items-center justify-center gap-3 rounded-sm border border-slate-300 bg-white text-sm font-semibold transition-colors hover:border-slate-900">
      <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
      {t("google")}
    </button>
  );
}

function ProfilePicker({ onPick }) {
  const { t, lang } = usePrefs();
  return (
    <div className="rise" data-testid="profile-picker">
      <h2 className="font-display text-3xl font-bold text-slate-900">{t("chooseSpace")}</h2>
      {["clients", "management"].map((g) => (
        <div key={g} className="mt-7">
          <div className="mb-3 flex items-center justify-between">
            <span className="eyebrow">{t(g)}</span>
            {g === "management" && <span className="flex items-center gap-1 text-[10px] text-slate-500"><Lock size={10} />{t("approvalRequired")}</span>}
          </div>
          <div className="grid gap-2.5 sm:grid-cols-3">
            {Object.entries(PROFILES).filter(([, p]) => p.group === g).map(([key, p]) => (
              <button key={key} onClick={() => onPick(key)} data-testid={`profile-${key}`}
                className="card-lift group flex flex-col items-start rounded-sm border border-slate-200 bg-white p-4 text-left">
                <span className="flex h-9 w-9 items-center justify-center rounded-sm bg-[#0F172A] text-[#E5C46B] transition-colors group-hover:bg-[#B8860B] group-hover:text-white"><p.icon size={17} /></span>
                <span className="mt-3 text-sm font-bold text-slate-900">{p[L(lang)][0]}</span>
                <span className="mt-1 text-[11px] leading-snug text-slate-500">{p[L(lang)][1]}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ExtraField({ name, type, required, f, setF }) {
  const { t, lang } = usePrefs();
  const cats = useCategories();
  const lbl = `${t(FIELD_LABEL[name])}${required ? "" : ` (${t("optional")})`}`;
  if (type === "trades") {
    const toggle = (k) => setF({ ...f, trades: f.trades.includes(k) ? f.trades.filter((x) => x !== k) : [...f.trades, k] });
    return (
      <div><Label>{lbl}</Label>
        <div className="mt-1.5 grid max-h-40 grid-cols-2 gap-1.5 overflow-y-auto rounded-sm border bg-white p-2.5" data-testid="register-trades">
          {cats.trade.map((c) => <label key={c.key} className="flex items-center gap-2 text-xs"><Checkbox checked={f.trades.includes(c.key)} onCheckedChange={() => toggle(c.key)} data-testid={`register-trade-${c.key}`} />{catName(c, lang)}</label>)}
        </div>
      </div>
    );
  }
  const C = type === "textarea" ? Textarea : Input;
  return (
    <div><Label>{lbl}</Label>
      <C type={type === "textarea" ? undefined : type} required={required} value={f[name]} onChange={(e) => setF({ ...f, [name]: e.target.value })} data-testid={`register-${name}-input`} className="mt-1.5 rounded-sm bg-white" />
    </div>
  );
}

const EMPTY = { name: "", email: "", password: "", phone: "", company: "", trades: [], siret: "", city: "", properties_count: "", pro_card: "", position: "", message: "" };

function AuthForm({ role, onBack, onMfa }) {
  const { setUser } = useAuth();
  const { t, lang } = usePrefs();
  const nav = useNavigate();
  const p = PROFILES[role];
  const [mode, setMode] = useState("login");
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && role === "prestataire" && !f.trades.length) return toast.error(lang === "fr" ? "Choisissez au moins un métier" : "Choose at least one trade");
    setBusy(true);
    try {
      const body = mode === "login" ? { email: f.email, password: f.password, role }
        : { ...f, role, properties_count: f.properties_count === "" ? null : Number(f.properties_count) };
      const { data } = await api.post(mode === "login" ? "/auth/login" : "/auth/register", body);
      if (data.mfa_token) return onMfa(data);
      setUser(data);
      nav("/");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const input = (k, type, testId) => <Input type={type} required value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={testId} className="mt-1.5 h-11 rounded-sm bg-white" />;
  return (
    <div className="rise" data-testid={`auth-panel-${role}`}>
      <button onClick={onBack} className="mb-6 flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900" data-testid="change-space-button"><ArrowLeft size={13} />{t("changeSpace")}</button>
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-sm bg-[#0F172A] text-[#E5C46B]"><p.icon size={20} /></span>
        <div><div className="eyebrow">{t("spaceOf")}</div><h2 className="font-display text-3xl font-bold text-slate-900" data-testid="auth-space-title">{p[L(lang)][0]}</h2></div>
      </div>
      <div className="mt-6 grid grid-cols-2 rounded-sm border border-slate-200 bg-white p-1 text-sm font-semibold">
        {["login", "register"].map((m) => <button key={m} type="button" onClick={() => setMode(m)} data-testid={`auth-tab-${m}`} className={`rounded-sm py-2 transition-colors ${mode === m ? "bg-[#0F172A] text-white" : "text-slate-500 hover:text-slate-900"}`}>{t(m)}</button>)}
      </div>
      {mode === "register" && p.group === "management" && <p className="mt-3 flex items-center gap-1.5 rounded-sm bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="approval-notice"><Lock size={12} />{t("approvalRequired")}</p>}
      <div className="mt-5"><GoogleButton role={role} /></div>
      <div className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-widest text-slate-400"><div className="h-px flex-1 bg-slate-200" />{t("or")}<div className="h-px flex-1 bg-slate-200" /></div>
      <form onSubmit={submit} className="space-y-3.5" data-testid="auth-form">
        {mode === "register" && <div><Label>{t("name")}</Label>{input("name", "text", "register-name-input")}</div>}
        <div><Label>{t("email")}</Label>{input("email", "email", "login-email-input")}</div>
        <div><Label>{t("password")}</Label>{input("password", "password", "login-password-input")}</div>
        {mode === "register" && p.fields.map(([k, type, req]) => <ExtraField key={k} name={k} type={type} required={req} f={f} setF={setF} />)}
        <button type="submit" disabled={busy} data-testid="auth-submit-button" className="flex h-11 w-full items-center justify-center gap-2 rounded-sm bg-[#0F172A] text-sm font-semibold text-white transition-colors hover:bg-[#B8860B] disabled:opacity-60">
          {busy && <Loader2 size={16} className="animate-spin" />}{mode === "login" ? t("login") : t("register")}
        </button>
      </form>
    </div>
  );
}

export default function Signin() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [mfa, setMfa] = useState(() => JSON.parse(sessionStorage.getItem("gst_mfa") || "null"));
  useEffect(() => { sessionStorage.removeItem("gst_mfa"); }, []);
  const { t, lang, setLang } = usePrefs();
  const [role, setRole] = useState(null);
  const p = role && PROFILES[role];
  return (
    <div className="grid min-h-screen lg:grid-cols-[1fr_1.1fr]">
      <div className="grain relative hidden overflow-hidden lg:block">
        <img key={role || "base"} src={p?.img || DEFAULT_HERO} alt="Architecture" className="rise absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/40 to-slate-950/20" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <div className="font-display text-3xl font-bold tracking-[0.15em]">GESTIMMO</div>
          <div className="max-w-xl">
            <div className="eyebrow mb-4 !text-[#E5C46B]">{p ? `${t("spaceOf")} ${p[L(lang)][0]}` : t("platformTag")}</div>
            <h1 className="font-display text-5xl font-bold leading-[1.05] xl:text-6xl">{p ? p[L(lang)][1] : t("heroTitle")}</h1>
            {!p && <p className="mt-6 max-w-md text-base text-slate-200">{t("heroSub")}</p>}
          </div>
          <div className="text-xs italic tracking-wide text-slate-300" data-testid="login-signature">Conçue par ELOUNGOU ÉTIENNE LANDRY</div>
        </div>
      </div>
      <div className="flex flex-col bg-[#F8FAFC]">
        <div className="flex items-center justify-between p-6">
          <span className="font-display text-xl font-bold tracking-[0.12em] lg:invisible">GESTIMMO</span>
          <div className="flex gap-1 text-[11px] font-bold">
            {LANGS.map(([l]) => <button key={l} onClick={() => setLang(l)} data-testid={`login-lang-${l}`} className={`px-2 py-1 uppercase ${lang === l ? "text-slate-900 underline decoration-[#B8860B] underline-offset-4" : "text-slate-400"}`}>{l}</button>)}
          </div>
        </div>
        <div className="flex flex-1 items-start justify-center px-6 pb-12 sm:items-center">
          <div className={`w-full ${role ? "max-w-md" : "max-w-2xl"}`}>
            {mfa ? <MfaStep challenge={mfa} onCancel={() => setMfa(null)} onSuccess={(u) => { setUser(u); nav("/"); }} />
              : role ? <AuthForm key={role} role={role} onBack={() => setRole(null)} onMfa={setMfa} /> : <ProfilePicker onPick={setRole} />}
            <p className="mt-10 text-[11px] italic text-slate-400 lg:hidden">Conçue par ELOUNGOU ÉTIENNE LANDRY</p>
          </div>
        </div>
      </div>
    </div>
  );
}
